import { useEffect, useRef, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingSpotOrderMethod } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import { zodResolver } from '@hookform/resolvers/zod'
import type { FieldErrors } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Alert,
  type BoxProps,
  FieldErrorText as EcFieldErrorText,
  FieldLabel as EcFieldLabel,
  FieldRoot,
  HStack,
  Input,
  Link,
  NativeSelectField,
  NativeSelectIndicator,
  NativeSelectRoot,
  RadioGroupItemControl,
  RadioGroupItemHiddenInput,
  RadioGroupRoot,
  RadioGroupItem as RawRadioGroupItem,
  RadioGroupItemText as RawRadioGroupItemText,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { BidLimitHint } from '~/components/bidding/common/BidLimitHint'
import {
  IssuerSelectorView,
  useIssuerOptions,
} from '~/components/bidding/common/IssuerSelector'
import {
  isAreaSelectableForIssuer,
  settledIssuerOption,
} from '~/components/bidding/common/bidIssuer'
import { initialBidArea } from '~/components/bidding/common/lastBidArea'
import { FormSelect } from '~/components/common/FormSelect'
import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { useBidLimits } from '~/hooks/bidding/useBidLimits'
import { useCompanyInfo } from '~/hooks/useCompanyInfo'
import { resolveFormErrorAlertMessage } from '~/libs/formErrors'
import { convertAreaLabel } from '~/models/area'
import {
  isMarketOrderAllowed,
  perBidVolumeLimitExceeded,
  resolveAreaSideLimit,
} from '~/models/bidLimits'
import {
  JEPX_VOLUME_CONSTRAINT,
  wrapNumberInputChange,
} from '~/models/numberInputConstraint'
import { FeeAutoConsiderHint } from '~/pages/SpotBidding/components/FeeAutoConsiderHint'
import { MemoFields } from '~/pages/SpotBidding/components/MemoFields'
import { useSpotBidIssuerForm } from '~/pages/SpotBidding/hooks/useSpotBidIssuerForm'
import { useSpotFeeAutoConsider } from '~/pages/SpotBidding/hooks/useSpotFeeAutoConsider'
import { useSpotFeeAutoConsiderPreview } from '~/pages/SpotBidding/hooks/useSpotFeeAutoConsiderPreview'
import { composeEmissionComment } from '~/pages/SpotBidding/models/emissionLinking'
import {
  isSpotFeePreviewBlocking,
  resolveSpotFeeSubmission,
  spotOrderPriceForChecks,
  spotPriceEntryText,
} from '~/pages/SpotBidding/models/feePreview'
import { SPOT_KOMA_OPTIONS } from '~/pages/SpotBidding/models/komaOptions'
import type { SpotBidCreateFormInput } from '~/pages/SpotBidding/models/schema'
import { spotBidCreateFormSchema } from '~/pages/SpotBidding/models/schema'
import type { SpotBidCreateSubmission } from '~/pages/SpotBidding/models/spotBidWrite'
import {
  isSpotFeeAutoConsiderPending,
  priceInputConstraint,
} from '~/pages/SpotBidding/models/spotFeeAutoConsider'
import { selectableAreaCodes } from '~/pages/SpotBidding/models/tradableArea'
import { routePaths } from '~/routes/routePaths'

// chakra v3.29 + React 18 types の既知不具合で `FieldLabelProps` / `FieldErrorTextProps`
// (= `extends HTMLChakraProps<...>, UnstyledProp`) が `{ unstyled }` のみに collapse し、
// children を含む全 props を JSX で受け取れなくなる。 ecloud 由来の component をそのまま使い、
// 取りこぼした props (children / style) を補う BoxProps で型付けし直す。
const FieldLabel = EcFieldLabel as React.FC<BoxProps & { htmlFor?: string }>
const FieldErrorText = EcFieldErrorText as React.FC<BoxProps>
// 同じ collapse が `RadioGroupItem` / `RadioGroupItemText` の `children` にも及ぶ
// (= IndividualBidModal と同じ既知不具合)。
const RadioGroupItem = RawRadioGroupItem as React.FC<
  BoxProps & { value: string; disabled?: boolean }
>
const RadioGroupItemText = RawRadioGroupItemText as React.FC<BoxProps>
// フォーム側の `orderMethod` は zod schema 由来の生 number (= `direction`/`areaCode`
// 等の他 enum field と同じ規約)。 enum member と直接比較すると
// @typescript-eslint/no-unsafe-enum-comparison に触れるため number 化する
// (`Supply48Data/models/supply48QueryParams.ts` と同じ回避)。
const ORDER_METHOD_MARKET: number = BiddingSpotOrderMethod.MARKET

type Props = {
  /** コマリボンでクリックされたコマ番号 (= 1〜48)。 form の timeCode 初期値になる。 */
  slotIndex: number
  /**
   * ページの受渡日。 発注主体区分の選択肢とエリアの選択可否をこの日の広域マスタで求める
   * (保存時の受渡契約コード解決と同じ断面。 #8842)。
   */
  deliveryDate: Date
  /**
   * ページのエリアフィルタで単一エリアを指定している場合、 その AreaCode。
   * 指定があれば エリア欄を自動設定する (AC-BSP-123)。
   */
  defaultAreaCode?: AreaCode
  /**
   * カバー率 (= 充足率) に沿った推奨入札量 (MW)。 = そのコマの調達不足分
   * (need - cover)。 指定があれば 数量欄を自動設定する。
   */
  defaultVolumeMw?: number
  /**
   * 開いた時の発注主体区分 (= 画面上部ツールバーで選択中の区分。 AC-BSP-005d)。
   * 未指定・選択肢に無い時は [[IssuerSelector]] が優先順の最上位を既定選択する。
   */
  defaultIssuerType?: LicenseType
  /**
   * 取引不可エリア (`ListTradableAreas` の取引可能エリアに無いエリア)。 選択肢から除く
   * (AC-BSP-005b。 発注できないエリアの札を作らせない)。 未取得時は空集合 = 制限しない。
   */
  disabledAreas?: ReadonlySet<AreaCode>
  /**
   * 業務エリア設定で許可された JEPX 対象エリア (AC-BSP-019 / `~/hooks/useBusinessAreaFilter`)。
   * これ以外のエリアは選択肢に残して選べなくし「業務エリア対象外」 を添える (AC-BSP-005b)。
   */
  areaCodes: readonly AreaCode[]
  onClose: () => void
  /**
   * 作成完了時に、 validation 済みのフォーム入力値を親へ渡す。
   * 親 (= NormalBidTab) はこれを RPC で永続化する。
   *
   * 永続化の完了まで await できるよう Promise を返す (issue #3511): 返り値を待たずに
   * modal を閉じていた頃は、 応答が返る前の 2 回目のクリックで Create が 2 発飛び、
   * 同一受渡日に DRAFT 申請セットが 2 件でき上がる経路になっていた。
   */
  /**
   * 登録する札。 `feeAutoConsider` が付いた札の価格は希望価格で、 入札価格は backend が算出する
   * (AC-BSP-004b)。 付かない札の価格は入札価格そのもの。
   */
  onCreated: (values: SpotBidCreateSubmission) => Promise<void>
}

/** エリアを入れる form の field。 `useSelectableAreas` へ渡す (module 定数で参照を固定)。 */
const AREA_FIELD_NAMES = ['areaCode'] as const

/** 新規作成だけのモーダルなので、 候補外・取引不可でも残すエリアは無い。 */
const NO_KEPT_AREA_CODES: readonly AreaCode[] = []

/** Select 用: string → number coerce、 空文字 → undefined */
const enumSetValueAs = (raw: unknown): number | undefined => {
  if (raw === '' || raw === null || raw === undefined) return undefined
  const num = typeof raw === 'number' ? raw : Number(raw)
  return Number.isNaN(num) ? undefined : num
}

/**
 * コマリボンのコマをクリックした時に開く「入札追加（個別）」モーダル。
 *
 * design HTML `design/bidding/spot.html` の `#bid-new-modal` を React で再現する。
 * 発注主体は全入札 UI 共通の [[IssuerSelector]] で選ぶ (= 発注主体区分)。
 * 「入札追加」ボタンから開く [[IndividualBidModal]] と同じ項目・同じ選択肢になる。
 *
 * TODO(rpc): 単発入札作成 RPC が proto 未定義のため、 作成自体は現状 mock（親で札一覧へ
 * append + toast）。 RPC 追加後に CreateBiddingSpotBid 等を呼ぶ。
 */
export const SpotBidCreateModal = ({
  slotIndex,
  deliveryDate,
  defaultAreaCode,
  defaultVolumeMw,
  defaultIssuerType = LicenseType.UNSPECIFIED,
  disabledAreas,
  areaCodes,
  onClose,
  onCreated,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // 選択肢はページが同じ受渡日で取得済みなので、 開いた時点でキャッシュから届く。
  const issuerOptions = useIssuerOptions(deliveryDate)
  // 開いた区分で選べないエリアは初期値にしない (AC-BSP-123)。 区分を初期値で渡すと
  // 既定選択の onChange が走らず、 区分の切替で外す経路 (AC-BSP-005b) も通らない。
  const [initialAreaCode] = useState<AreaCode | undefined>(() =>
    initialBidArea(
      'spot',
      defaultAreaCode,
      (code) =>
        selectableAreaCodes(
          disabledAreas,
          areaCodes,
          NO_KEPT_AREA_CODES,
        ).includes(code) &&
        isAreaSelectableForIssuer(
          settledIssuerOption(issuerOptions, defaultIssuerType),
          code,
        ),
    ),
  )

  const {
    control,
    register,
    handleSubmit,
    watch,
    clearErrors,
    setError,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<SpotBidCreateFormInput>({
    resolver: zodResolver(spotBidCreateFormSchema),
    defaultValues: {
      // 発注主体区分は画面上部ツールバーの区分で開く。 それが無い・選択肢に無い時は、
      // 選択肢が届いた時点で優先順の最上位を既定選択する
      // ([[IssuerSelector]] の `selectsDefault`、 AC-BSP-005d)。 モーダルは開くたびに
      // mount されるので、 defaultValues は開いた時点の値になる。
      licenseType: defaultIssuerType,
      timeCode: slotIndex,
      direction: Side.BUY,
      areaCode: initialAreaCode,
      // カバー率の不足分を推奨入札量として自動設定 (0 / 充足済みは空欄)。
      volumeMw:
        defaultVolumeMw !== undefined && defaultVolumeMw > 0
          ? String(defaultVolumeMw)
          : undefined,
      // 価格種別 (指値 / 成行、 AC-BSP-004a)。 指値を既定にする (= 従来どおりの挙動)。
      orderMethod: BiddingSpotOrderMethod.LIMIT,
    },
  })

  // ----- 発注主体区分 -----
  // 選んだ区分をそのまま `licenseType` (= 書込の issuer_type) として送る。
  const watchedLicenseType = watch('licenseType') as LicenseType

  // ----- 入札制限インラインヒント (design (B))。SPOT_NORMAL の実設定値を照合する -----
  const {
    limits: bidLimits,
    marketOrderPolicyEntries,
    isLoading: bidLimitsLoading,
  } = useBidLimits(BidSegment.SPOT_NORMAL)
  const watchedAreaCode = watch('areaCode')
  const watchedDirection = watch('direction')
  const watchedPrice = watch('priceYenPerKwh')
  const watchedVolume = watch('volumeMw')
  const watchedOrderMethod = watch('orderMethod')
  const isMarket = watchedOrderMethod === ORDER_METHOD_MARKET
  // 成行注文許可 (AC-BSP-004a、 SoT は `bid-limits.md` §1-1)。 エリア未選択 / 取得中・
  // 取得失敗はいずれも false (= fail-safe、 [[isMarketOrderAllowed]] 参照)。
  const marketAllowed =
    watchedAreaCode !== undefined &&
    isMarketOrderAllowed(
      marketOrderPolicyEntries,
      BidSegment.SPOT_NORMAL,
      watchedAreaCode,
    )
  // エリア + 売/買 が確定した時点で制限を引く (どちらか未確定なら undefined = 非表示)。
  const resolvedLimit =
    watchedAreaCode !== undefined && watchedDirection !== undefined
      ? resolveAreaSideLimit(bidLimits, watchedAreaCode, watchedDirection)
      : undefined
  // 成行は価格を持たない (AC-BSP-050) ので、 価格の入札制限ヒントからは除く。
  const hintPrice =
    !isMarket && watchedPrice !== undefined && watchedPrice !== ''
      ? Number(watchedPrice)
      : undefined
  const hintVolume =
    watchedVolume !== undefined && watchedVolume !== ''
      ? Number(watchedVolume)
      : undefined

  // ----- 手数料自動考慮 (AC-BDL-016 / AC-BSP-004b / AC-BSP-106)。 ON の間は入力欄が希望価格で、
  // backend が算出した入札価格を補足表示する (#8109)。 単価を使えない間は OFF として扱う。 -----
  // 開く時だけ条件付きで描画される (NormalBidTab) ので、 mount している間は開いている。
  const { feeAutoConsider: openedFee, isSettingsPending } =
    useSpotFeeAutoConsider(true)
  const priceEntryText = isMarket ? undefined : spotPriceEntryText(watchedPrice)
  const {
    fee: feeAutoConsider,
    preview: feePreview,
    invalidInput: isPriceRejected,
  } = useSpotFeeAutoConsiderPreview({
    isOpen: true,
    fee: openedFee,
    input:
      watchedAreaCode !== undefined &&
      watchedDirection !== undefined &&
      priceEntryText !== undefined
        ? {
            areaCode: watchedAreaCode,
            direction: watchedDirection,
            desiredPriceYenPerKwh: priceEntryText,
          }
        : undefined,
  })
  const limitHintPrice = spotOrderPriceForChecks({
    fee: feeAutoConsider,
    preview: feePreview,
    enteredPrice: hintPrice,
  })

  // 決定 2026-09-16 (2-B): 設定 (ON/OFF) が読めるまで登録ボタンを押せなくする
  // (codex-review 指摘 finding 5197f7e0876f)。 手数料単価だけを取得できない間は OFF として
  // 扱い、 止めない (AC-BSP-004b)。 入力が最新のプレビューに追いつくまで・プレビューが画面の扱いと
  // 食い違う間も押せない (確認画面の表示と保存値を一致させる。 `docs/principles.md` ルール 1)。
  const isFeeAutoConsiderBlocking =
    isSettingsPending || isSpotFeePreviewBlocking(feePreview, isMarket)
  // 設定の取得中は価格欄を触れなくする (入札価格として入力した値が、 ON の確定で希望価格に
  // 読み替わって加減算されるのを防ぐ。 AC-BSP-004b)。 扱いを固定した後は読み替わらないので、
  // 再取得の失敗では触れなくしない。
  const isPriceEntryPending = isSpotFeeAutoConsiderPending(feeAutoConsider)
  // ON の価格欄は希望価格で、 算出した入札価格で判定する (#11466 Q117 = B)。 買い 999 になる希望価格 999.03 を
  // 入れられるよう、 値域の両端を手数料単価ぶん広げる ([[priceInputConstraint]])。
  const priceConstraint = priceInputConstraint(feeAutoConsider)
  // 成行選択時は価格入力欄を無効化する (AC-BSP-004a)。 表示中は強制的に "0" に
  // 揃えておく (実送信時の最終防衛は `spotCreateFormToBidInput` 側)。
  useEffect(() => {
    if (isMarket) {
      setValue('priceYenPerKwh', '0', { shouldValidate: true })
    }
  }, [isMarket, setValue])

  // 成行選択中にエリアを変更して成行注文許可が無くなった場合、 指値へ自動フォールバック
  // する (design `spot.html` `_updateBnewPriceType` の「成行選択中に不可へ→指値へ
  // fallback」と同じ挙動、 codex-review 指摘)。 取得中 (`bidLimitsLoading`) の間は
  // 判定不能であって「不許可」ではないため、 取得完了を待ってから評価する。
  useEffect(() => {
    if (!bidLimitsLoading && isMarket && !marketAllowed) {
      setValue('orderMethod', BiddingSpotOrderMethod.LIMIT, {
        shouldValidate: true,
      })
    }
  }, [bidLimitsLoading, isMarket, marketAllowed, setValue])

  // 会社情報 (登録小売電気事業者 ID)。 排出係数紐付けの判別コメント組み立てに使う。
  const companyInfo = useCompanyInfo()

  // 永続化中フラグ。 state は保存ボタンの disabled 表示、 ref は再入判定に使う
  // (issue #3511)。 ref が要るのは、 同一 tick 内の連打では 2 回目の submit ハンドラが
  // 1 回目と同じ render の closure を見るため state では防げないから。
  const [isSaving, setIsSaving] = useState(false)
  const savingRef = useRef(false)

  const onSubmit = async (values: SpotBidCreateFormInput): Promise<void> => {
    if (savingRef.current) return
    // 排出係数紐付け ON なら判別コメント完成形を memo_jepx へ格納する
    // (AC-BSP-086)。 OFF なら memo_jepx は空 (= JEPX へ note を送らない)。
    const linked = values.emissionLinked === true && companyInfo.registered
    // 手数料自動考慮 (AC-BSP-004b / AC-BSP-106): 入札価格は backend が算出する。
    const submission = resolveSpotFeeSubmission(t, {
      price: values.priceYenPerKwh,
      isMarket: values.orderMethod === ORDER_METHOD_MARKET,
      fee: feeAutoConsider,
      preview: feePreview,
      rejectedByBackend: isPriceRejected,
    })
    if (submission.kind === 'error') {
      setError('priceYenPerKwh', { message: submission.message })
      return
    }
    // hold の間は登録ボタンを押せない。
    if (submission.kind === 'hold') return
    savingRef.current = true
    setIsSaving(true)
    try {
      // 永続化の完了を待ってから閉じる。 待たずに閉じていた頃は in-flight 中の
      // 再クリックで Create が 2 発飛んでいた (issue #3511)。
      await onCreated({
        ...values,
        feeAutoConsider: submission.feeAutoConsider,
        memoJepx: linked
          ? composeEmissionComment(
              companyInfo.retailerId,
              values.emissionAlpha ?? '',
            )
          : undefined,
      })
    } finally {
      savingRef.current = false
      setIsSaving(false)
    }
    onClose()
  }

  // 永続化中の閉じる操作を全部塞ぐ。 footer の isCancelDisabled だけでは足りない —
  // 共通 Modal は Escape と × を有効にしたまま onClose を呼ぶので (Modal の JSDoc)、
  // 保存中に Escape / × で閉じるとこの component ごと unmount され savingRef が
  // 失われる。 直後に同じコマを開き直すと新しい ref は false なので 2 発目の
  // Create が飛ぶ (issue #3511 の二重 Create が別経路で復活する)。
  const handleClose = (): void => {
    if (savingRef.current) return
    onClose()
  }

  // 業務エリアの候補のうち取引可能エリアだけを選択させる (= 発注できないエリアの札を
  // 作らせない)。
  const issuerForm = useSpotBidIssuerForm({
    deliveryDate,
    active: true,
    mode: 'create',
    readOnly: false,
    issuerField: 'licenseType',
    issuerValue: watchedLicenseType,
    areaFields: AREA_FIELD_NAMES,
    areas: [watchedAreaCode],
    candidateAreaCodes: areaCodes,
    disabledAreas,
    keepAreaCodes: NO_KEPT_AREA_CODES,
    getValues,
    setValue,
    setError,
    handleSubmit,
  })
  const { areaSelectOptionsFor, selected: selectedIssuer } = issuerForm

  const handleSave = (): void => {
    // 二重防御 (footer の isSaveDisabled と同一条件)。 決定 2-B。
    if (isFeeAutoConsiderBlocking) return
    issuerForm.submit(
      (data) => {
        // AC-BSP-103: 上限数量(1札) 超過は保存をブロックする (= BidLimitHint は
        // 事前チェックの表示のみで submit を止めないため、 ここで別途弾く)。
        // クランプは行わない (bid-limits.md §1-2 参照)。
        const submitVolume = Number(data.volumeMw)
        const exceededQtyMax = perBidVolumeLimitExceeded(
          bidLimits,
          data.areaCode,
          data.direction,
          submitVolume,
        )
        if (exceededQtyMax !== undefined) {
          setError('volumeMw', {
            message: t('page:biddingSpot.form.volumeExceedsPerBidLimit', {
              max: exceededQtyMax,
              value: submitVolume,
            }),
          })
          return
        }
        return onSubmit(data)
      },
      (formErrors: FieldErrors<SpotBidCreateFormInput>) => {
        const alertMessage = resolveFormErrorAlertMessage(
          formErrors,
          t('page:biddingSpot.serverError.validation'),
        )
        if (alertMessage !== undefined) {
          setError('root.serverError', { message: alertMessage })
        } else {
          // 項目脇の表示だけに切り替わるので、 直前の失敗で出した alert を消す。
          clearErrors('root.serverError')
        }
      },
    )
  }
  // AC-3304-2: 数量・価格入力欄は JEPX 範囲外・不正な小数点桁の入力を受け付けない。
  const volumeReg = register('volumeMw')
  const priceReg = register('priceYenPerKwh')

  const footer = (
    // 永続化中は保存・キャンセルとも無効化する (= 二重 Create / 途中離脱の抑止、 issue #3511)。
    <SaveCancelFooter
      isCancelDisabled={isSaving}
      isPending={isSaving}
      isSaveDisabled={isSaving || isFeeAutoConsiderBlocking}
      onCancel={handleClose}
      onSave={handleSave}
      saveLabel={t('common:action.create')}
    />
  )

  return (
    <Modal
      footer={footer}
      isOpen
      onClose={handleClose}
      scrollBehavior="inside"
      title={t('page:biddingSpot.modal.bidCreateTitle')}
    >
      <VStack align="stretch" gap={4}>
        {errors.root?.serverError !== undefined && (
          <Alert status="error" title={errors.root.serverError.message} />
        )}

        {/* 発注主体区分。 全入札 UI 共通の [[IssuerSelector]] を使う。
            以前はここだけ「事業者マスタ × ライセンス種別」の 2 段 select で、 同じ
            通常入札でも「入札追加」から開くモーダルと項目が違っていた (issue #5366)。 */}
        <IssuerSelectorView
          errorMessage={errors.licenseType?.message}
          onChange={issuerForm.onChange}
          options={issuerForm.options}
          value={watchedLicenseType}
        />

        {/* 売/買 */}
        <FieldRoot invalid={errors.direction !== undefined} required>
          <FieldLabel>{t('page:biddingSpot.form.direction')}</FieldLabel>
          <NativeSelectRoot>
            <NativeSelectField
              {...register('direction', { setValueAs: enumSetValueAs })}
            >
              <option value={Side.BUY}>{t('page:biddingSpot.side.buy')}</option>
              <option value={Side.SELL}>
                {t('page:biddingSpot.side.sell')}
              </option>
            </NativeSelectField>
            <NativeSelectIndicator />
          </NativeSelectRoot>
          <FieldErrorText>{errors.direction?.message}</FieldErrorText>
        </FieldRoot>

        {/* コマ（コマリボンのクリックで初期値が入る）。 番号入力 + 時間帯の添え書きではなく
            「28 (13:30〜14:00)」の select で選ぶ (issue #5365 Des 裁定 2026-08-18: 時間前入札と
            同じ全画面共通表記。 「入札追加 ▾ → 個別作成」の IndividualBidModal と同じ選択肢)。 */}
        <FieldRoot invalid={errors.timeCode !== undefined} required>
          <FieldLabel>{t('page:biddingSpot.form.timeCode')}</FieldLabel>
          <FormSelect
            width="100%"
            aria-label={t('page:biddingSpot.form.timeCode')}
            control={control}
            name="timeCode"
            numeric
            options={SPOT_KOMA_OPTIONS}
          />
          <FieldErrorText>{errors.timeCode?.message}</FieldErrorText>
        </FieldRoot>

        {/* エリア */}
        <FieldRoot invalid={errors.areaCode !== undefined} required>
          <FieldLabel>{t('page:biddingSpot.form.areaCode')}</FieldLabel>
          {/* 幅は売/買・コマと同じくモーダルいっぱい (#10125。 160px 固定だと
              「北海道 (広域マスタ未登録)」 などの選択肢が折り返していた。
              個別作成 IndividualBidModal と同じ幅)。 */}
          <NativeSelectRoot>
            <NativeSelectField
              {...register('areaCode', { setValueAs: enumSetValueAs })}
            >
              <option value="">
                {t('page:biddingSpot.form.areaCodeUnselected')}
              </option>
              {areaSelectOptionsFor(selectedIssuer, watchedAreaCode).map(
                ({ area, label, disabled }) => (
                  <option key={area} disabled={disabled} value={area}>
                    {label}
                  </option>
                ),
              )}
            </NativeSelectField>
            <NativeSelectIndicator />
          </NativeSelectRoot>
          <FieldErrorText>{errors.areaCode?.message}</FieldErrorText>
        </FieldRoot>

        {/* 数量 (MW。 AC-BSP-053 で コマ別の入札量は MW 建て) */}
        {/* `alignItems="stretch"`: DS `Input` は内部を縦 Stack + `Field.Root` で包むため、
            外の `FieldRoot` (既定 `align-items: flex-start`) のままだと `width="100%"` が
            縮んだ内側に対して解決されて細くなる (SingularDayForm と同じ対処、 #10125)。 */}
        <FieldRoot
          alignItems="stretch"
          invalid={errors.volumeMw !== undefined}
          required
        >
          <FieldLabel htmlFor="spotBid-volumeMw">
            {t('page:biddingSpot.form.volumeMw')}
          </FieldLabel>
          <Input
            width="100%"
            max={JEPX_VOLUME_CONSTRAINT.max}
            min={JEPX_VOLUME_CONSTRAINT.min}
            step={JEPX_VOLUME_CONSTRAINT.step}
            type="number"
            {...volumeReg}
            id="spotBid-volumeMw"
            onChange={wrapNumberInputChange(volumeReg, JEPX_VOLUME_CONSTRAINT)}
          />
          <FieldErrorText>{errors.volumeMw?.message}</FieldErrorText>
        </FieldRoot>

        {/* 価格種別 (指値 / 成行、 AC-BSP-004a)。 成行はエリア別の成行注文許可設定
            (`bid-limits.md` §1-1 が SoT) に従い選択肢をグレーアウトし、 選択時は
            価格入力欄を無効化する。 [[IndividualBidModal]] と同じ項目・選択肢。 */}
        <FieldRoot invalid={errors.orderMethod !== undefined} required>
          <FieldLabel>{t('page:biddingSpot.form.orderMethod')}</FieldLabel>
          <RadioGroupRoot
            onValueChange={(details: { value: string | null }) => {
              setValue(
                'orderMethod',
                details.value === 'market'
                  ? BiddingSpotOrderMethod.MARKET
                  : BiddingSpotOrderMethod.LIMIT,
                { shouldValidate: true },
              )
            }}
            value={isMarket ? 'market' : 'limit'}
          >
            <HStack gap={4}>
              <RadioGroupItem value="limit">
                <RadioGroupItemHiddenInput />
                <RadioGroupItemControl />
                <RadioGroupItemText>
                  {t('page:biddingSpot.orderMethod.limit')}
                </RadioGroupItemText>
              </RadioGroupItem>
              <RadioGroupItem disabled={!marketAllowed} value="market">
                <RadioGroupItemHiddenInput />
                <RadioGroupItemControl />
                <RadioGroupItemText
                  color={marketAllowed ? undefined : 'Text.disabled'}
                >
                  {t('page:biddingSpot.orderMethod.market')}
                  {watchedAreaCode !== undefined && !marketAllowed
                    ? t('page:biddingSpot.orderMethod.marketDisabledSuffix')
                    : ''}
                </RadioGroupItemText>
              </RadioGroupItem>
            </HStack>
          </RadioGroupRoot>
          {/* 成行不許可エリアの案内 (AC-BSP-004a 規定文言)。 */}
          {watchedAreaCode !== undefined && !marketAllowed && (
            <Text marginTop={1} color="Text.midEmphasis" fontSize="sm">
              {t('common:bidding.marketOrderHint.prefix', {
                area: convertAreaLabel(watchedAreaCode),
              })}
              {/* 作成ダイアログの中から別の管理画面へ移るリンクは新しいタブで開く
                  (UI 辞書 `components/link-target.md`「別途指定」 2 行目 = 同じタブだと入力中の
                  内容が確認なしに失われる。 2026-10-16 Des 判断。 8080 も target="_blank")。
                  内部遷移なので external icon は付けない。 */}
              <Link asChild>
                <RouterLink
                  rel="noopener noreferrer"
                  target="_blank"
                  to={routePaths.adminBidLimits.path}
                >
                  {t('common:bidding.marketOrderHint.link')}
                </RouterLink>
              </Link>
              {t('common:bidding.marketOrderHint.suffix')}
            </Text>
          )}
          <FieldErrorText>{errors.orderMethod?.message}</FieldErrorText>
        </FieldRoot>

        {/* 価格 (円/kWh) */}
        <FieldRoot
          alignItems="stretch"
          invalid={errors.priceYenPerKwh !== undefined}
          required
        >
          <FieldLabel htmlFor="spotBid-priceYenPerKwh">
            {t(
              feeAutoConsider.kind === 'on'
                ? 'page:biddingSpot.form.desiredPriceYenPerKwh'
                : 'page:biddingSpot.form.bidPrice',
            )}
          </FieldLabel>
          <Input
            width="100%"
            disabled={isMarket || isPriceEntryPending}
            max={priceConstraint.max}
            min={priceConstraint.min}
            step={priceConstraint.step}
            type="number"
            {...priceReg}
            id="spotBid-priceYenPerKwh"
            onChange={wrapNumberInputChange(priceReg, priceConstraint)}
          />
          <FieldErrorText>{errors.priceYenPerKwh?.message}</FieldErrorText>
          {/* 手数料自動考慮 (AC-BDL-016 / AC-BSP-004b) の入札価格プレビュー。 backend が算出した
              入札価格が範囲外の間は AC-BSP-106 の 2 行の文言に置き換える。
              成行は価格を入力しない (価格欄が無効) ので、 どの扱いでも案内を出さない
              (design の補足表示は成行で空。 AC-BSP-004b は成行を対象外とする)。 */}
          {!isMarket && (
            <FeeAutoConsiderHint
              fee={feeAutoConsider}
              isSettingsUnavailable={isSettingsPending}
              preview={feePreview}
            />
          )}
        </FieldRoot>

        {/* 入札制限インラインヒント (design (B))。エリア + 売/買 確定後に表示。 */}
        {/* ON の間の価格欄は希望価格なので、 入札制限の上下限価格は手数料単価を加減算した
            入札価格で判定する (AC-BSP-107 ※。 個別・ブロックと同じ)。 */}
        {watchedAreaCode !== undefined && watchedDirection !== undefined && (
          <BidLimitHint
            limit={resolvedLimit}
            price={limitHintPrice}
            volumeMw={hintVolume}
          />
        )}

        {/* 排出係数紐付け (memo_jepx) + メモ（自由記述、 memo_free）。 */}
        <MemoFields
          emission={{
            registered: companyInfo.registered,
            retailerId: companyInfo.retailerId,
            fetchFailed: companyInfo.isError,
            isLoading: companyInfo.isLoading,
            linked: watch('emissionLinked') ?? false,
            alpha: watch('emissionAlpha') ?? '',
            alphaError: errors.emissionAlpha?.message,
            onLinkedChange: (linked) => setValue('emissionLinked', linked),
            onAlphaChange: (alpha) =>
              setValue('emissionAlpha', alpha, { shouldValidate: true }),
          }}
          freeError={errors.memoFree?.message}
          freeValue={watch('memoFree') ?? ''}
          idPrefix="spotBidCreate"
          registerFree={register('memoFree')}
        />
      </VStack>
    </Modal>
  )
}
